import type { FunctionComponent } from 'react';
import { useState } from 'react';
import classNames from 'classnames';

import { useMemberId } from '@trello/authentication';
import { longDateFormatter } from '@trello/dates/i18n';
import { forTemplate } from '@trello/legacy-i18n';
import { BusinessClassIcon } from '@trello/nachos/icons/business-class';
import type { BillingIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { BillingHistory } from 'app/src/components/BillingDetails/BillingHistory/BillingHistory';
import { useSubscriptionExpirationStatus } from 'app/src/components/WorkspaceBillingView/useSubscriptionExpirationStatus';
import { usePlanDetailsViewQuery } from './PlanDetailsViewQuery.generated';

import styles from './NuSkuBillingStatusBanner.less';

const format = forTemplate('billing_page_one');

interface NuSkuBillingStatusBannerProps {
  teamId: string;
  dtPricingAdjustmentExpiration: string | null | undefined;
}

export const NuSkuBillingStatusBanner: FunctionComponent<
  NuSkuBillingStatusBannerProps
> = ({ teamId, dtPricingAdjustmentExpiration }) => {
  const memberId = useMemberId();
  const [showBillingHistory, setShowBillingHistory] = useState(false);
  const toggleShowBillingHistory = () =>
    setShowBillingHistory(!showBillingHistory);
  const { data } = usePlanDetailsViewQuery({
    variables: {
      orgId: teamId,
      memberId,
    },
    skip: !teamId,
    waitOn: ['MemberHeader'],
  });

  const {
    paidPlanExpired,
    paidPlanDisabled,
    planType,
    planExpirationDate,
    freeTrialExpired,
    freeTrialExpirationDate,
  } = useSubscriptionExpirationStatus({
    orgId: teamId,
    paidAccount: data?.organization?.paidAccount,
  });

  const paidPlanEnded = paidPlanExpired || paidPlanDisabled;
  const showBillingStatusBanner = paidPlanEnded || freeTrialExpired;

  if (!showBillingStatusBanner) return null;

  const date = paidPlanEnded ? planExpirationDate : freeTrialExpirationDate;

  return (
    <div
      className={classNames(styles.banner)}
      data-testid={getTestId<BillingIds>('billing-status-banner')}
    >
      <div className={classNames(styles.summary)}>
        <BusinessClassIcon
          size="large"
          dangerous_className={styles.icon}
          block
        />
        <div className={styles.label}>
          <div className={styles.text}>
            {paidPlanEnded
              ? format(['billing-status-banner', 'workspace-has-ended'], {
                  productName:
                    planType === 'bc'
                      ? format('business-class-name')
                      : format('standard-name'),
                })
              : format(
                  ['billing-status-banner', 'your-free-trial-ended-on-date'],
                  {
                    productName: format('business-class-name'),
                    date: date ? longDateFormatter.format(new Date(date)) : '',
                  },
                )}
          </div>
          <div
            onClick={toggleShowBillingHistory}
            role="button"
            className={classNames(styles.link)}
          >
            {format([
              'billing-status-banner',
              showBillingHistory
                ? 'hide-billing-history'
                : 'show-billing-history',
            ])}
          </div>
        </div>
      </div>
      {showBillingHistory && (
        <div className={classNames(styles.details)}>
          <BillingHistory type={planType} accountId={teamId} />
        </div>
      )}
    </div>
  );
};
